<template>
  <div>
    <div class="ai-runner-selection">
      <div class="select is-fullwidth">
        <select v-model="selectedAIRunner" :disabled="loadingRunners">
          <option :value="null">{{ $t('select-ai-runner') }}</option>
          <option v-for="runner in runnerOptions" :key="runner.id" :value="runner">
            {{ runner.name }}
          </option>
        </select>
      </div>
      <div class="ai-runner-actions">
        <button class="button is-primary" @click="runAlgorithm" :disabled="!selectedAIRunner || loadingRunners">
          <span class="icon is-small"><i class="fas fa-play"></i></span>
          <span>{{ $t('button-run-ai') }}</span>
        </button>
        <button class="button is-light" @click="loadHistoricalJobs" :disabled="loadingRunners" :title="$t('refresh-jobs')">
          <span class="icon is-small"><i class="fas fa-sync-alt"></i></span>
        </button>
        <button
          v-if="failedJobs.length > 0"
          class="button is-light failed-jobs-button"
          @click="failedJobsPanelVisible = true"
          :title="$t('failed-ai-jobs')"
        >
          <span class="icon is-small"><i class="fas fa-history"></i></span>
        </button>
      </div>
    </div>

    <div v-if="activeJobs.length > 0" class="active-jobs">
      <div v-for="job in activeJobs" :key="job.sessionId" class="active-job">
        <i class="fas fa-spinner fa-spin"></i>
        <span>{{ $t('processing-ai-runner', { runnerName: job.runnerName }) }}</span>
      </div>
    </div>

    <algorithm-failed-jobs-panel
      :jobs="failedJobs"
      :visible="failedJobsPanelVisible"
      @toggle="failedJobsPanelVisible = !failedJobsPanelVisible"
    />

    <!-- Dynamic AI Result Panels -->
    <div class="panel" v-for="result in aiResults" :key="result.id">
      <div class="panel-title" @click="result.visible = !result.visible">
        <div class="panel-title-left">
          <label class="switch" @click.stop>
            <input type="checkbox" :checked="result.showAllAnnotations" @change="handlePanelToggle(result)">
            <span class="switch-slider"></span>
          </label>
          <span class="panel-name">{{ result.runnerName }}</span>
        </div>
        <span class="arrow" :class="{ open: result.visible }">▶</span>
      </div>

      <div v-if="result.visible" class="panel-body">
        <!-- AddingSync Results按钮 -->
        <div class="level is-mobile mb-2">
          <div class="level-left">
            <button v-if="result.needsResultSync" class="button is-small is-info" @click="syncResult(result.id)">
              {{ $t('sync-results') }}
            </button>
          </div>
          <div class="level-right">
            <button v-if="result.isGleason" class="button is-small is-light" @click="openGleasonReferenceDoc">
              <span class="icon is-small"><i class="fas fa-file-pdf"></i></span>
              <span>{{ $t('reference-doc') }}</span>
            </button>
          </div>
        </div>

        <article v-if="result.dataWarning" class="message is-danger ai-result-warning">
          <div class="message-body">
            {{ formatAiWarning(result.dataWarning) }}
          </div>
        </article>
        <article v-for="warning in result.warnings || []" :key="warning.code || warning.message" class="message is-danger ai-result-warning">
          <div class="message-body">
            {{ formatAiWarning(warning) }}
          </div>
        </article>

        <malaria-result-panel
          v-if="result.isMalaria"
          :result="result"
          :project="project"
          :index="index"
          :all-terms="allTerms"
          :ontology-terms="ontologyTerms"
          :images="images"
          :active-slice-id="activeSliceId"
          @heatmapChange="handleMalariaHeatmapChange"
          @overlayChange="handleMalariaOverlayChange"
          @settingsChange="handleMalariaSettingsChange"
          @select="selectAnnotation"
          @centerView="centerView"
          @deletion="handleDeletion"
          @updateTermsOrTracks="updateTermsOrTracks"
        />
        <leprosy-result-panel
          v-else-if="result.isLeprosy"
          :result="result"
          :project="project"
          :index="index"
          :all-terms="allTerms"
          :ontology-terms="ontologyTerms"
          :images="images"
          :active-slice-id="activeSliceId"
          @heatmapChange="handleLeprosyHeatmapChange"
          @overlayChange="handleLeprosyOverlayChange"
          @settingsChange="handleLeprosySettingsChange"
          @select="selectAnnotation"
          @centerView="centerView"
          @deletion="handleDeletion"
          @updateTermsOrTracks="updateTermsOrTracks"
        />
        <gleason-result-panel
          v-else-if="result.isGleason"
          :result="result"
          :project="project"
          :index="index"
          :all-terms="allTerms"
          :ontology-terms="ontologyTerms"
          :images="images"
          @select="selectAnnotation"
          @centerView="centerView"
          @deletion="handleDeletion"
          @updateTermsOrTracks="updateTermsOrTracks"
          @toggleTermVisibility="toggleTermVisibilityByName"
          @loadAlgorithmAnnotations="handleLoadAlgorithmAnnotations"
        />
        <nottingham-result-panel
          v-else-if="result.isNottingham"
          :result="result"
          :project="project"
          :index="index"
          :all-terms="allTerms"
          :ontology-terms="ontologyTerms"
          :images="images"
          @select="selectAnnotation"
          @centerView="centerView"
          @deletion="handleDeletion"
          @updateTermsOrTracks="updateTermsOrTracks"
          @toggleTermVisibility="toggleTermVisibilityByName"
          @loadAlgorithmAnnotations="handleLoadAlgorithmAnnotations"
        />
      </div>
    </div>

    <!-- Terms Panel -->
    <div class="panel">
      <div class="panel-title" @click="manualTermsPanelVisible = !manualTermsPanelVisible">
        <span>{{ $t('user-annotations') }}</span>
        <span class="arrow" :class="{ open: manualTermsPanelVisible }">▶</span>
      </div>
      <div v-if="manualTermsPanelVisible" class="panel-body">
        <user-annotation-terms-panel
          :index="index"
          :project="project"
          :algorithm-ontology-ids="algorithmOntologyIds"
          @select="selectAnnotation"
          @centerView="centerView"
          @delete="handleDeletion"
          @updateTermsOrTracks="updateTermsOrTracks"
          @open-add-terms="openSelectOntologyModal"
        />
      </div>
    </div>

  </div>
</template>


<script>
import { AIRunner, AIAlgorithmJob, Annotation } from '@/api';
import Cytomine from '@/api/cytomine';
import AlgorithmFailedJobsPanel from './AlgorithmFailedJobsPanel';
import GleasonResultPanel from './GleasonResultPanel';
import LeprosyResultPanel from './LeprosyResultPanel';
import MalariaResultPanel from './MalariaResultPanel';
import NottinghamResultPanel from './NottinghamResultPanel';
import UserAnnotationTermsPanel from './UserAnnotationTermsPanel';
import { createMockLeprosyPayload, createMockMalariaPayload, findAIModelAdapter, recomputeMalariaResult } from './ai-models';
import { Action, listAnnotationsInGroup, updateAnnotationLinkProperties, updateTermProperties, updateTrackProperties } from '@/utils/annotation-utils';

const ALGORITHM_PANEL_IMAGE_CACHE = {};
const LEPROSY_DEMO_RUNNER_NAME = 'Leprosy AFB Detection — WSI Pipeline';
const LEPROSY_DEMO_RUNNER = Object.freeze({
  id: -22001,
  name: LEPROSY_DEMO_RUNNER_NAME,
  ontology: null,
  demo: true,
  modelType: 'leprosy'
});

export default {
  name: 'algorithm-panel',
  components: {
    AlgorithmFailedJobsPanel,
    GleasonResultPanel,
    LeprosyResultPanel,
    MalariaResultPanel,
    NottinghamResultPanel,
    UserAnnotationTermsPanel
  },
  props: {
    project: Object,
    index: String,
  },
  data() {
    return {
      // AI Runner相关数据
      aiRunners: [],
      selectedAIRunner: null,
      loadingRunners: true,
      manualTermsPanelVisible: true,

      activeJobs: [],
      failedJobs: [],
      failedJobsPanelVisible: false,
      jobLoadRequestId: 0,
      aiResults: [],
      fakeLeprosyJobTimers: [],
      algorithmAnnotationsPageSize: 24,
      useMockAIJobResults: false,
      useMockMalariaJobResult: false,
      useMockLeprosyJobResult: false,
    };
  },

  computed: {
    viewerModule() {
      return this.$store.getters['currentProject/viewerModulePath'];
    },
    imageModule() {
      return this.$store.getters['currentProject/imageModulePath'](this.index);
    },
    imageWrapper() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index];
    },
    image() {
      return this.imageWrapper?.imageInstance;
    },
    images() {
      return this.image ? [this.image] : [];
    },
    activeSliceId() {
      return this.imageWrapper?.activeSlices?.[0]?.id;
    },
    ontologyTerms() {
      return this.imageWrapper.style.ontologyTerms;
    },
    allTerms() {
      return this.$store.getters[this.imageModule + 'terms'] || [];
    },
    imageId() {
      return this.imageWrapper?.imageInstance?.id;
    },
    malariaConfidenceAnnotationsByJob() {
      return this.imageWrapper?.malariaConfidenceAnnotationsByJob || {};
    },
    malariaPanelStateByJob() {
      return this.imageWrapper?.malariaPanelStateByJob || {};
    },
    malariaVisualizationSettings() {
      return this.imageWrapper?.malariaVisualizationSettings || {};
    },
    imageGroupId() {
      return this.$store.getters[this.imageModule + 'imageGroupId'];
    },
    copiedAnnot: {
      get() {
        return this.$store.getters['currentProject/currentViewer'].copiedAnnot;
      },
      set(annot) {
        this.$store.commit(this.viewerModule + 'setCopiedAnnot', annot);
      }
    },
    algorithmOntologyIds() {
      return this.aiRunners
        .map(runner => runner.ontology)
        .filter(id => id !== null && id !== undefined);
    },
    runnerOptions() {
      return [
        ...this.aiRunners,
        LEPROSY_DEMO_RUNNER
      ];
    }
  },
  watch: {
    aiResults(newVal) {
      this.updateActiveJobIds(newVal);
    },
    imageId(newImageId, oldImageId) {
      if (newImageId && newImageId !== oldImageId) {
        this.refreshImageScopedData();
      }
    },
    malariaPanelStateByJob: {
      handler() {
        this.refreshMalariaResultsFromStore();
      }
    },
    malariaVisualizationSettings: {
      deep: true,
      handler() {
        this.refreshMalariaResultsFromStore();
      }
    }
  },
  methods: {
    imageCacheKey() {
      return String(this.imageId || '');
    },
    persistImageScopedCache() {
      const cacheKey = this.imageCacheKey();
      if (!cacheKey) {
        return;
      }
      ALGORITHM_PANEL_IMAGE_CACHE[cacheKey] = {
        aiRunners: this.aiRunners,
        activeJobs: this.activeJobs,
        failedJobs: this.failedJobs,
        aiResults: this.aiResults,
        selectedAIRunner: this.selectedAIRunner
      };
    },
    restoreImageScopedCache() {
      const cacheKey = this.imageCacheKey();
      if (!cacheKey || !ALGORITHM_PANEL_IMAGE_CACHE[cacheKey]) {
        return false;
      }
      const cached = ALGORITHM_PANEL_IMAGE_CACHE[cacheKey];
      this.aiRunners = cached.aiRunners || [];
      this.activeJobs = cached.activeJobs || [];
      this.failedJobs = cached.failedJobs || [];
      this.aiResults = cached.aiResults || [];
      this.selectedAIRunner = cached.selectedAIRunner || null;
      return true;
    },
    resetImageScopedState() {
      this.clearFakeLeprosyJobTimers();
      this.activeJobs = [];
      this.failedJobs = [];
      this.failedJobsPanelVisible = false;
      this.aiResults = [];
    },
    updateActiveJobIds(results = this.aiResults) {
      const activeJobs = (results || []).filter(r => this.hasVisibleTerms(r)).map(r => r.id);
      this.$store.commit(this.imageModule + 'setActiveJobIds', activeJobs);
    },

    async refreshImageScopedData() {
      this.jobLoadRequestId++;
      this.resetImageScopedState();
      if (this.imageId) {
        const restored = this.restoreImageScopedCache();
        if (restored) {
          this.refreshMalariaResultsFromStore();
        }
        if (!restored || !this.aiResults.length) {
          await this.loadHistoricalJobs();
        }
      }
    },

    async fetchAIRunners() {
      this.loadingRunners = true;
      try {
        this.aiRunners = await AIRunner.fetchAll();
        this.persistImageScopedCache();
      } catch (error) {
        console.error('Failed to fetch AI runners:', error);
        this.$buefy.toast.open({
          message: this.$t('notif-error-load-ai-runners'),
          type: 'is-danger'
        });
      } finally {
        this.loadingRunners = false;
      }
    },

    runAlgorithm() {
      if (!this.selectedAIRunner) {
        this.$buefy.toast.open({
          message: this.$t('please-select-ai-runner'),
          type: 'is-danger'
        });
        return;
      }

      return this.$buefy.dialog.confirm({
        title: this.$t('confirm-ai-processing'),
        message: this.$t('confirm-run-ai-on-image-message', { runnerName: this.selectedAIRunner.name }),
        type: 'is-primary',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: async () => {
          try {
            if (this.isLeprosyDemoRunner(this.selectedAIRunner)) {
              this.startFakeLeprosyRunningJob();
              return;
            }
            const requestData = {
              airunnerId: this.selectedAIRunner.id,
              projectId: parseInt(this.project.id),
              imageId: parseInt(this.imageId),
              // TODO: parameters field might be needed depending on the runner
            };

            let response = await AIAlgorithmJob.runAlgorithm(requestData);

            this.$buefy.toast.open({
              message: this.$t('notif-success-ai-processing-started'),
              type: 'is-success'
            });

            this.activeJobs.push({
              sessionId: response.session_id,
              runnerName: this.selectedAIRunner.name
            });
            // Auto reload after initiating
            this.loadHistoricalJobs();

            this.selectedAIRunner = null;
          } catch (error) {
            this.$buefy.toast.open({
              message: this.$t('notif-error-ai-processing-start'),
              type: 'is-danger'
            });
            console.error('AI processing failed:', error);
          }
        }
      });
    },
    isLeprosyDemoRunner(runner) {
      return runner?.demo === true && runner?.modelType === 'leprosy';
    },
    startFakeLeprosyRunningJob() {
      this.$buefy.toast.open({
        message: this.$t('notif-success-ai-processing-started'),
        type: 'is-success'
      });

      const sessionId = `mock-leprosy-running-session-${Date.now()}`;
      this.activeJobs.push({
        sessionId,
        runnerName: LEPROSY_DEMO_RUNNER_NAME
      });
      this.scheduleFakeLeprosyJobResult(sessionId, this.imageId);
      this.persistImageScopedCache();
      this.selectedAIRunner = null;
    },
    scheduleFakeLeprosyJobResult(sessionId, imageId) {
      const timerId = setTimeout(() => {
        this.completeFakeLeprosyRunningJob(sessionId, imageId);
      }, 10000);
      this.fakeLeprosyJobTimers.push(timerId);
    },
    async completeFakeLeprosyRunningJob(sessionId, imageId) {
      if (String(imageId || '') !== String(this.imageId || '')) {
        return;
      }
      this.activeJobs = this.activeJobs.filter(job => job.sessionId !== sessionId);
      const job = this.createMockLeprosyJob();
      await this.integrateJobResult(job.id, job.runnerName, JSON.parse(job.data), false);
      const result = this.aiResults.find(aiResult => aiResult.id === job.id);
      if (result) {
        this.syncLeprosyVisualizationLayers(result);
      }
      this.persistImageScopedCache();
    },
    clearFakeLeprosyJobTimers() {
      (this.fakeLeprosyJobTimers || []).forEach(timerId => clearTimeout(timerId));
      this.fakeLeprosyJobTimers = [];
    },

    openSelectOntologyModal() {
      this.$eventBus.$emit('openAddOntologyModal');
    },

    async updateTermsOrTracks(annot) {
      let updatedAnnot = await annot.clone().fetch();
      updatedAnnot.imageGroup = this.imageGroupId;
      await updateTermProperties(updatedAnnot);
      await updateTrackProperties(updatedAnnot);
      await updateAnnotationLinkProperties(updatedAnnot);

      this.$eventBus.$emit('editAnnotation', updatedAnnot);
      this.$store.commit(this.imageModule + 'changeAnnotSelectedFeature', { indexFeature: 0, annot: updatedAnnot });
    },

    async handleDeletion(annot) {
      this.$store.commit(this.imageModule + 'addAction', { annot: annot, type: Action.DELETE });

      if (annot.group) {
        let editedAnnots = [];
        if (annot.annotationLink.length === 2) {
          let otherId = annot.annotationLink.filter(al => al.annotation !== annot.id)[0].annotation;
          let other = await Annotation.fetch(otherId);
          other.imageGroup = annot.imageGroup;
          await updateTermProperties(other);
          await updateTrackProperties(other);
          await updateAnnotationLinkProperties(other);

          editedAnnots = [other];
        } else {
          editedAnnots = await listAnnotationsInGroup(annot.project, annot.group);
        }
        editedAnnots.forEach(a => {
          this.$eventBus.$emit('editAnnotation', a);
          if (this.copiedAnnot && a.id === this.copiedAnnot.id) {
            let copiedAnnot = this.copiedAnnot.clone();
            copiedAnnot.annotationLink = a.annotationLink;
            copiedAnnot.group = a.group;
            this.copiedAnnot = copiedAnnot;
          }
        });
      }

      this.$eventBus.$emit('deleteAnnotation', annot);
    },

    selectAnnotation({ annot, options = {} }) {
      let index = (options.trySameView) ? this.index : null;
      this.$eventBus.$emit('selectAnnotation', { index, annot, center: true });
    },

    centerView({ annot, sameView = false }) {
      if (sameView) {
        this.$eventBus.$emit('selectAnnotation', { index: this.index, annot, center: true });
      } else {
        this.$eventBus.$emit('selectAnnotation', { index: null, annot, center: true });
      }
    },

    toggleTermVisibility(term) {
      this.$store.commit(this.imageModule + 'toggleOntologyTermVisibility', { termId: term.id });
    },
    toggleTermVisibilityByName(termName) {
      const term = this.getTermByName(termName);
      if (term) {
        this.toggleTermVisibility(term);
      }
    },
    areAllGleasonTermsVisible(result) {
      return result.annotations.every(annotation => {
        const term = this.allTerms.find(t => t.name === annotation.label);
        if (term && this.ontologyTerms[term.id]) {
          return this.ontologyTerms[term.id].visible;
        }
        return true;
      });
    },
    toggleAllNottinghamTerms(result) {
      const shouldShowAll = result.showAllAnnotations;

      // 获取所有需要处理的术语名称和对应的rawData
      const termsToToggle = [];


      Object.keys(result.rawData).forEach(key => {
        if (result.rawData[key].percentage !== undefined || result.rawData[key].count !== undefined) {
          termsToToggle.push({ name: key, data: result.rawData[key] });
        }
      });

      // 切换所有术语的可见性
      termsToToggle.forEach(({ name, data }) => {
        const term = this.allTerms.find(t => t.name === name);
        if (term) {
          // 只有当当前状态与目标状态不一致时才切换
          const currentVisible = this.ontologyTerms[term.id] ? this.ontologyTerms[term.id].visible : true;
          if (currentVisible !== shouldShowAll) {
            this.toggleTermVisibility(term);
          }
          // 同步更新本地的showOnImage状态
          data.showOnImage = shouldShowAll;
        }
      });
    },
    toggleAllGleasonTerms(result) {
      const shouldShowAll = result.showAllAnnotations;

      result.annotations.forEach(annotation => {
        const term = this.allTerms.find(t => t.name === annotation.label);
        if (term) {
          const currentVisible = this.ontologyTerms[term.id] ? this.ontologyTerms[term.id].visible : true;
          if (currentVisible !== shouldShowAll) {
            this.toggleTermVisibility(term);
          }
          annotation.showOnImage = shouldShowAll;
        }
      });
    },
    openGleasonReferenceDoc() {
      window.open('/reporting/Prostate%20Gleason%20Scoring%20Analysis%20Calculation%20and%20Logic.pdf', '_blank', 'noopener');
    },
    getTermByName(termName) {
      const normalizedName = String(termName).toLowerCase();
      return this.allTerms.find(term => String(term.name).toLowerCase() === normalizedName);
    },
    ensureAlgorithmAnnotationState(result, termName) {
      if (!result.algorithmAnnotations) {
        this.$set(result, 'algorithmAnnotations', {});
      }
      if (!result.algorithmAnnotations[termName]) {
        this.$set(result.algorithmAnnotations, termName, {
          expanded: false,
          loading: false,
          error: false,
          items: [],
          page: 0,
          hasMore: false
        });
      }
      return result.algorithmAnnotations[termName];
    },
    normalizeAlgorithmAnnotation(raw) {
      const annot = new Annotation(raw);
      const basePath = `${Cytomine.instance.host}${Cytomine.instance.basePath}`;
      annot.cropURL = annot.cropURL || `${basePath}annotation/${annot.id}/crop.png`;
      annot.smallCropURL = annot.smallCropURL || `${basePath}annotation/${annot.id}/crop.png?maxSize=256`;
      return annot;
    },
    malariaDetectionsForJob(jobId) {
      return this.malariaConfidenceAnnotationsByJob?.[jobId] || [];
    },
    malariaPanelStateForJob(jobId) {
      return this.malariaPanelStateByJob?.[jobId] || null;
    },
    applyStoredMalariaState(result) {
      const storedDetections = this.malariaDetectionsForJob(result.id);
      const storedPanelState = this.malariaPanelStateForJob(result.id);
      const settings = this.malariaVisualizationSettings || {};
      if (storedPanelState) {
        return {
          ...result,
          showAllAnnotations: settings.showAllAnnotations ?? result.showAllAnnotations,
          detections: storedDetections.length ? storedDetections : (result.detections || []),
          detectionsLoaded: storedDetections.length > 0,
          warnings: storedPanelState.warnings || result.warnings || [],
          derived: storedPanelState.derived,
          thresholds: {
            ...result.thresholds,
            ...storedPanelState.thresholds,
            byType: {
              ...(result.thresholds?.byType || {}),
              ...(storedPanelState.thresholds?.byType || {})
            }
          },
          summary: {
            ...result.summary,
            ...storedPanelState.summary,
            rawParasiteCount: result.summary?.rawParasiteCount ?? storedPanelState.summary?.rawParasiteCount
          },
          density: {
            ...result.density,
            ...storedPanelState.density
          },
          heatmap: {
            ...result.heatmap,
            ...storedPanelState.heatmap,
            points: []
          },
          overlay: {
            ...result.overlay,
            ...storedPanelState.overlay,
            annotations: [],
            points: []
          },
          report: {
            ...result.report,
            status: storedPanelState.report?.status || result.report?.status
          }
        };
      }
      return recomputeMalariaResult({
        ...result,
        showAllAnnotations: settings.showAllAnnotations ?? result.showAllAnnotations,
        detections: storedDetections.length ? storedDetections : (result.detections || []),
        detectionsLoaded: storedDetections.length > 0,
        thresholds: {
          ...result.thresholds,
          ...(settings.thresholds || {}),
          byType: {
            ...(result.thresholds?.byType || {}),
            ...(settings.thresholds?.byType || {})
          }
        },
        heatmap: {
          ...result.heatmap,
          ...(settings.heatmap || {}),
          byTypeVisible: {
            ...(result.heatmap?.byTypeVisible || {}),
            ...(settings.heatmap?.byTypeVisible || {})
          }
        },
        overlay: {
          ...result.overlay,
          ...(settings.overlay || {}),
          byTypeVisible: {
            ...(result.overlay?.byTypeVisible || {}),
            ...(settings.overlay?.byTypeVisible || {})
          }
        }
      }, { includeVisualizations: false });
    },
    refreshMalariaResultsFromStore() {
      this.aiResults = this.aiResults.map(result => {
        if (!this.isMalariaResult(result)) {
          return result;
        }
        return this.applyStoredMalariaState(result);
      });
      this.persistImageScopedCache();
    },
    async loadAlgorithmAnnotations(result, termName, append = false) {
      const state = this.ensureAlgorithmAnnotationState(result, termName);
      const term = this.getTermByName(termName);
      if (!term) {
        state.items = [];
        state.hasMore = false;
        state.error = false;
        return;
      }

      state.loading = true;
      state.error = false;

      const nextPage = append ? state.page + 1 : 0;

      try {
        const { data } = await Cytomine.instance.api.post('annotation/search.json', {
          images: String(this.imageId),
          max: this.algorithmAnnotationsPageSize,
          multipleTerm: false,
          noTerm: false,
          offset: nextPage * this.algorithmAnnotationsPageSize,
          project: this.project.id,
          showAlgo: true,
          showDefault: true,
          showGIS: true,
          showImage: false,
          showImageGroup: true,
          showTerm: true,
          showWKT: true,
          term: term.id,
          jobs: String(result.id)
        });
        const items = (data.collection || []).map(raw => this.normalizeAlgorithmAnnotation(raw));
        state.items = append ? [...state.items, ...items] : items;
        state.page = nextPage;
        state.hasMore = data.totalPages ? nextPage + 1 < data.totalPages : items.length === this.algorithmAnnotationsPageSize;
      } catch (error) {
        console.error('Failed to load algorithm annotations:', error);
        state.error = true;
        state.hasMore = false;
      } finally {
        state.loading = false;
      }
    },
    handleLoadAlgorithmAnnotations({ result, termName, append = false }) {
      this.loadAlgorithmAnnotations(result, termName, append);
    },
    isGleasonResult(result) {
      return result.isGleason === true;
    },
    isNottinghamResult(result) {
      return result.isNottingham === true;
    },
    isMalariaResult(result) {
      return result.isMalaria === true;
    },
    isLeprosyResult(result) {
      return result.isLeprosy === true;
    },
    handleMalariaHeatmapChange(heatmap) {
      this.$store.dispatch(this.imageModule + 'setMalariaHeatmap', heatmap);
    },
    handleMalariaOverlayChange(overlay) {
      this.$store.dispatch(this.imageModule + 'setMalariaDetectionOverlay', overlay);
    },
    handleMalariaSettingsChange(settings) {
      this.$store.dispatch(this.imageModule + 'setMalariaVisualizationSettings', settings);
    },
    handleLeprosyOverlayChange(overlay) {
      this.$store.dispatch(this.imageModule + 'setMalariaDetectionOverlay', overlay);
    },
    handleLeprosyHeatmapChange(heatmap) {
      this.$store.dispatch(this.imageModule + 'setMalariaHeatmap', heatmap);
    },
    handleLeprosySettingsChange() {
      // Leprosy panel persistence can be added once the backend result shape is stable.
    },
    syncLeprosyVisualizationLayers(result) {
      const heatmapPoints = result.heatmap?.points || [];
      const overlayAnnotations = result.overlay?.annotations || result.overlay?.points || [];
      this.handleLeprosyHeatmapChange({
        ...result.heatmap,
        visible: result.showAllAnnotations && result.heatmap?.visible && heatmapPoints.length > 0,
        zIndex: 2
      });
      this.handleLeprosyOverlayChange({
        ...result.overlay,
        visible: result.showAllAnnotations && result.overlay?.visible && overlayAnnotations.length > 0,
        annotations: overlayAnnotations,
        points: overlayAnnotations,
        zIndex: 3
      });
    },
    handlePanelToggle(result) {
      result.showAllAnnotations = !result.showAllAnnotations;
      if (this.isNottinghamResult(result)) {
        // Nottingham Grading结果：控制术语显示/隐藏
        this.toggleAllNottinghamTerms(result);
      } else if (this.isGleasonResult(result)) {
        this.toggleAllGleasonTerms(result);
      } else if (this.isMalariaResult(result)) {
        this.$store.dispatch(this.imageModule + 'setMalariaVisualizationSettings', {
          showAllAnnotations: result.showAllAnnotations
        });
        this.handleMalariaHeatmapChange({
          ...result.heatmap,
          visible: result.showAllAnnotations && result.heatmap.visible,
          byTypeVisible: result.heatmap.byTypeVisible,
          zIndex: 2
        });
        this.handleMalariaOverlayChange({
          ...result.overlay,
          visible: result.showAllAnnotations && result.overlay.visible,
          zIndex: 3
        });
      } else if (this.isLeprosyResult(result)) {
        this.handleLeprosyHeatmapChange({
          ...result.heatmap,
          visible: result.showAllAnnotations && result.heatmap.visible,
          zIndex: 2
        });
        this.handleLeprosyOverlayChange({
          ...result.overlay,
          visible: result.showAllAnnotations && result.overlay.visible,
          zIndex: 3
        });
      }
      this.updateActiveJobIds();
    },
    hasVisibleTerms(result) {
      if (this.isNottinghamResult(result)) {
        // Nottingham Grading结果：检查术语可见性
        const termNames = [];

        Object.keys(result.rawData).forEach(key => {
          if (result.rawData[key].percentage !== undefined || result.rawData[key].count !== undefined) {
            termNames.push(key);
          }
        });

        return termNames.some(termName => {
          const term = this.allTerms.find(t => t.name === termName);
          if (term && this.ontologyTerms[term.id]) {
            return this.ontologyTerms[term.id].visible;
          }
          return true; // 如果找不到对应的ontology term，视为可见
        });
      } else if (this.isGleasonResult(result)) {
        return result.annotations.some(annotation => {
          const term = this.allTerms.find(t => t.name === annotation.label);
          if (term && this.ontologyTerms[term.id]) {
            return this.ontologyTerms[term.id].visible;
          }
          return true;
        });
      } else if (this.isMalariaResult(result)) {
        return false;
      } else if (this.isLeprosyResult(result)) {
        return false;
      }
      return false;
    },

    // Polling removed in favor of manual refresh
    async integrateJobResult(jobId, runnerName, jobData, needsResultSync = false) {
      let annotationsReport = null;
      // try {
      //   let response = await Cytomine.instance.api.get(`job/${jobId}/annotations_for_report.json`);
      //   annotationsReport = response.data;
      // } catch (error) {
      //   console.error('Error fetching job annotations report:', error);
      // }

      let nextResult = null;
      const adapter = findAIModelAdapter(runnerName);
      if (adapter) {
        nextResult = adapter.format(jobId, runnerName, jobData, needsResultSync);
        if (this.isMalariaResult(nextResult)) {
          nextResult = this.applyStoredMalariaState(nextResult);
        }
      } else if (runnerName === 'Breast Cancer Nottingham Grading — WSI Inference Pipeline') {
        nextResult = this.formatNottinghamResults(jobId, runnerName, jobData, annotationsReport, needsResultSync);
      } else if (runnerName === 'Prostate Biopsy Gleason Grading — WSI Inference Pipeline') {
        nextResult = this.formatGleasonResults(jobId, runnerName, jobData || annotationsReport, needsResultSync);
      }

      if (nextResult) {
        this.upsertAIResult(nextResult);
      }
    },
    upsertAIResult(nextResult) {
      const existingIndex = this.aiResults.findIndex(result => result.id === nextResult.id);
      if (existingIndex === -1) {
        this.aiResults.push(nextResult);
        this.updateActiveJobIds();
        this.persistImageScopedCache();
        return;
      }

      const existingResult = this.aiResults[existingIndex];
      nextResult.visible = existingResult.visible;
      nextResult.activeTab = existingResult.activeTab;
      nextResult.showAllAnnotations = existingResult.showAllAnnotations;

      if (existingResult.sectionCollapsed && nextResult.sectionCollapsed) {
        nextResult.sectionCollapsed = {
          ...nextResult.sectionCollapsed,
          ...existingResult.sectionCollapsed
        };
      }

      if (existingResult.measurements && nextResult.measurements) {
        nextResult.measurements = {
          ...nextResult.measurements,
          ...existingResult.measurements
        };
      }

      if (existingResult.report && nextResult.report) {
        nextResult.report = {
          ...nextResult.report,
          ...existingResult.report
        };
      }

      if (existingResult.thresholds && nextResult.thresholds) {
        nextResult.thresholds = {
          ...nextResult.thresholds,
          ...existingResult.thresholds
        };
      }

      if (existingResult.heatmap && nextResult.heatmap) {
        nextResult.heatmap = {
          ...nextResult.heatmap,
          ...existingResult.heatmap
        };
      }

      if (existingResult.overlay && nextResult.overlay) {
        nextResult.overlay = {
          ...nextResult.overlay,
          ...existingResult.overlay
        };
      }

      if (nextResult.isMalaria) {
        nextResult = this.applyStoredMalariaState(nextResult);
      }

      if (existingResult.algorithmAnnotations) {
        nextResult.algorithmAnnotations = existingResult.algorithmAnnotations;
      }

      this.$set(this.aiResults, existingIndex, nextResult);
      this.updateActiveJobIds();
      this.persistImageScopedCache();
    },
    createMockMalariaJob() {
      return {
        id: 11990,
        sessionId: 'mock-malaria-session',
        airunnerId: 990,
        runnerName: 'Malaria Detection — Thick Blood Film WSI Pipeline',
        projectId: Number(this.project.id),
        imageId: this.imageId,
        parameters: 'null',
        source: 'mock/malaria/thick-blood-film.svs',
        imageServer: 'mock-server',
        statusCode: 200,
        status: 'Completed',
        message: this.$t('mock-malaria-processing-completed'),
        data: JSON.stringify(createMockMalariaPayload()),
        created: Date.now(),
        updated: Date.now()
      };
    },
    shouldUseMockLeprosyJobResult() {
      if (this.useMockLeprosyJobResult) {
        return true;
      }
      const imageName = [
        this.image?.filename,
        this.image?.originalFilename,
        this.image?.name,
        this.image?.instanceFilename,
        this.image?.uploadedFilename
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return ['leprosy', 'lepra', 'hansen', 'afb', 'bacilli'].some(identifier => imageName.includes(identifier));
    },
    createMockLeprosyJob() {
      return {
        id: 22001,
        sessionId: 'mock-leprosy-session',
        airunnerId: 220,
        runnerName: 'Leprosy AFB Detection — WSI Pipeline',
        projectId: Number(this.project.id),
        imageId: this.imageId,
        parameters: 'null',
        source: 'mock/leprosy/leprosy-afb-demo.svs',
        imageServer: 'mock-server',
        statusCode: 200,
        status: 'Completed',
        message: this.$t('mock-leprosy-processing-completed'),
        data: JSON.stringify(createMockLeprosyPayload()),
        created: Date.now(),
        updated: Date.now()
      };
    },
    formatGleasonResults(jobId, runnerName, data, needsResultSync = false) {
      const supportedTerms = ['gleason3', 'gleason4', 'gleason5', 'stroma', 'benign'];
      const emptyMetric = { area: 0 };
      const minPatternArea = 0.001;
      const metricByTerm = {};

      supportedTerms.forEach(termName => {
        const item = data && typeof data[termName] === 'object' && !Array.isArray(data[termName])
          ? data[termName]
          : emptyMetric;
        metricByTerm[termName] = {
          area: Number(item.area) || 0
        };
      });

      const areaByTerm = Object.fromEntries(
        supportedTerms.map(termName => [termName, metricByTerm[termName].area])
      );
      const totalMalignantArea = areaByTerm.gleason3 + areaByTerm.gleason4 + areaByTerm.gleason5;
      const totalClassifiedArea = supportedTerms.reduce((sum, termName) => sum + areaByTerm[termName], 0);
      const tumorPercentage = totalClassifiedArea > 0 ? (totalMalignantArea / totalClassifiedArea) * 100 : 0;
      const patternPercentages = {
        gleason3: totalMalignantArea > 0 ? (areaByTerm.gleason3 / totalMalignantArea) * 100 : 0,
        gleason4: totalMalignantArea > 0 ? (areaByTerm.gleason4 / totalMalignantArea) * 100 : 0,
        gleason5: totalMalignantArea > 0 ? (areaByTerm.gleason5 / totalMalignantArea) * 100 : 0,
        stroma: totalClassifiedArea > 0 ? (areaByTerm.stroma / totalClassifiedArea) * 100 : 0,
        benign: totalClassifiedArea > 0 ? (areaByTerm.benign / totalClassifiedArea) * 100 : 0
      };
      const malignantPercentageSum = patternPercentages.gleason3 + patternPercentages.gleason4 + patternPercentages.gleason5;
      let dataWarning = '';

      if (totalMalignantArea > 0 && Math.abs(malignantPercentageSum - 100) > 1) {
        dataWarning = {
          messageKey: 'ai-warning-malignant-pattern-percentages',
          messageParams: { percentage: malignantPercentageSum.toFixed(1) }
        };
      }

      const malignantPatterns = [
        {
          key: 'gleason3',
          pattern: 3,
          magnitude: areaByTerm.gleason3,
          percentage: patternPercentages.gleason3
        },
        {
          key: 'gleason4',
          pattern: 4,
          magnitude: areaByTerm.gleason4,
          percentage: patternPercentages.gleason4
        },
        {
          key: 'gleason5',
          pattern: 5,
          magnitude: areaByTerm.gleason5,
          percentage: patternPercentages.gleason5
        }
      ].filter(item => item.magnitude >= minPatternArea);

      malignantPatterns.sort((a, b) => {
        if (b.magnitude !== a.magnitude) return b.magnitude - a.magnitude;
        return b.pattern - a.pattern;
      });

      if (malignantPatterns.length === 0) {
        dataWarning = { messageKey: 'ai-warning-no-malignant-area' };
      }

      const primaryPattern = malignantPatterns.length > 0 ? malignantPatterns[0].pattern : 0;
      let secondaryPattern = primaryPattern;
      const secondaryCandidate = malignantPatterns.find(item => item.pattern !== primaryPattern && item.percentage >= 5);
      if (secondaryCandidate) {
        secondaryPattern = secondaryCandidate.pattern;
      }

      let tertiaryPattern = 0;
      const tertiaryCandidate = malignantPatterns
        .filter(item => item.pattern > Math.max(primaryPattern, secondaryPattern) && item.percentage >= 5)
        .sort((a, b) => b.pattern - a.pattern)[0];
      if (tertiaryCandidate) {
        tertiaryPattern = tertiaryCandidate.pattern;
      }

      const totalScore = primaryPattern && secondaryPattern ? primaryPattern + secondaryPattern : 0;
      const gleasonScoreLabel = primaryPattern && secondaryPattern ? `${primaryPattern}+${secondaryPattern}` : '';
      let gradeGroup = 0;
      if (gleasonScoreLabel === '3+3') gradeGroup = 1;
      else if (gleasonScoreLabel === '3+4') gradeGroup = 2;
      else if (gleasonScoreLabel === '4+3') gradeGroup = 3;
      else if (['4+4', '3+5', '5+3'].includes(gleasonScoreLabel)) gradeGroup = 4;
      else if (['4+5', '5+4', '5+5'].includes(gleasonScoreLabel)) gradeGroup = 5;

      const gradeGroupLabel = gradeGroup ? `Grade Group ${gradeGroup}` : 'N/A';

      let newResult = {
        id: jobId,
        runnerName: runnerName,
        needsResultSync,
        dataWarning,
        visible: true,
        isGleason: true,
        activeTab: 0,
        showAllAnnotations: true,
        sectionCollapsed: {
          summary: false,
          report: false
        },
        algorithmAnnotations: {},
        rawData: data,
        annotations: [],
        areas: [],
        measurements: {
          tumorLength: 0, biopsyLength: 0, showTumorLength: true, showBiopsyLength: true
        },
        scoring: {
          primaryPattern,
          secondaryPattern,
          tertiaryPattern,
          totalScore,
          gleasonScoreLabel,
          gradeGroup,
          gradeGroupLabel,
          tumorPercentage: tumorPercentage.toFixed(1),
          totalMalignantArea: totalMalignantArea.toFixed(3),
          totalClassifiedArea: totalClassifiedArea.toFixed(3),
          malignantPercentageSum: malignantPercentageSum.toFixed(1),
          minPatternAreaThreshold: minPatternArea.toFixed(3),
          patternThresholdPercentage: '5.0',
          calculatedPercentages: {
            gleason3: patternPercentages.gleason3.toFixed(1),
            gleason4: patternPercentages.gleason4.toFixed(1),
            gleason5: patternPercentages.gleason5.toFixed(1),
            stroma: patternPercentages.stroma.toFixed(1),
            benign: patternPercentages.benign.toFixed(1)
          }
        },
        report: {
          gradeGroup,
          gradeGroupLabel,
          gleasonScore: totalScore,
          gleasonScoreLabel,
          gleason5: Number(patternPercentages.gleason5.toFixed(1)),
          gleason4: Number(patternPercentages.gleason4.toFixed(1)),
          gleason3: Number(patternPercentages.gleason3.toFixed(1)),
          cribriform: areaByTerm.gleason4 > 0 ? 'Present' : 'Absent',
          tertiaryPattern,
          tertiaryPatternLabel: tertiaryPattern ? `Pattern ${tertiaryPattern}` : 'None',
          tumorQuantification: Number(tumorPercentage.toFixed(1)),
          totalMalignantArea: Number(totalMalignantArea.toFixed(3)),
          totalClassifiedArea: Number(totalClassifiedArea.toFixed(3)),
          tumorLength: 0,
          biopsyLength: 0
        }
      };

      supportedTerms.forEach(termName => {
        const displayValue = areaByTerm[termName];
        const percentValue = patternPercentages[termName];

        newResult.annotations.push({
          label: termName,
          area: displayValue.toFixed(3),
          percent: `${percentValue.toFixed(1)}%`,
          present: areaByTerm[termName] > 0,
          color: this.getTermColorByName(termName) || '#1E88E5',
          showOnImage: true
        });
        newResult.areas.push({
          label: termName,
          value: displayValue.toFixed(3),
          color: this.getTermColorByName(termName) || '#1E88E5'
        });
      });

      newResult.showAllAnnotations = this.areAllGleasonTermsVisible(newResult);

      return newResult;
    },
    formatNottinghamResults(jobId, runnerName, data, annotationsReport, needsResultSync = false) {
      let newResult = {
        id: jobId,
        runnerName: runnerName,
        needsResultSync,
        visible: true,
        isNottingham: true,
        activeTab: 0,
        showAllAnnotations: true,
        // 折叠状态，默认展开
        sectionCollapsed: {
          summary: false,
          scoring: true,
          report: false
        },
        algorithmAnnotations: {},
        rawData: data, // Store raw data for display in Data tab
        annotations: [],
        areas: [],
        scoring: {
          tubulesScore: 0,
          pleomorphismScore: 0,
          mitosisScore: 0,
          totalScore: 0,
          grade: ''
        },
        report: {
          // 从原始数据中提取报告相关字段
          tubules: data.tubules || { count: 0, percentage: 0 },
          mitosis: data.mitosis || { count: 0, percentage: 0 },
          invasive: data.invasive || { count: 0, percentage: 0 },
          'Pleomorphism Score 1 Nuclei': data['Pleomorphism Score 1 Nuclei'] || { count: 0, percentage: 0 },
          'Pleomorphism Score 2 Nuclei': data['Pleomorphism Score 2 Nuclei'] || { count: 0, percentage: 0 },
          'Pleomorphism Score 3 Nuclei': data['Pleomorphism Score 3 Nuclei'] || { count: 0, percentage: 0 }
        }
      };

      if (annotationsReport && Object.keys(annotationsReport).length > 0) {
        for (let termName in annotationsReport) {
          let items = annotationsReport[termName];
          newResult.annotations.push({
            label: termName,
            expanded: false,
            percent: null,
            present: items.length > 0,
            color: this.getTermColorByName(termName) || '#1E88E5',
            showOnImage: true,
            items: items.map(ai => ({
              id: ai.id,
              showOnImage: true
            }))
          });
        }
      }

      // 将报告数据添加到区域中
      for (let key in newResult.report) {
        let rData = newResult.report[key];
        newResult.areas.push({
          label: key,
          value: rData.percentage !== undefined ? rData.percentage + '%' : rData.count,
          color: this.getTermColorByName(key) || '#9575CD'
        });
      }

      // 计算评分
      let tPct = newResult.report.tubules.percentage || 0;
      newResult.scoring.tubulesScore = tPct > 75 ? 1 : (tPct >= 10 ? 2 : 3);

      // 多形性评分以 percentage 为准，取占比最高的评分
      let p1 = newResult.report['Pleomorphism Score 1 Nuclei'].percentage || 0;
      let p2 = newResult.report['Pleomorphism Score 2 Nuclei'].percentage || 0;
      let p3 = newResult.report['Pleomorphism Score 3 Nuclei'].percentage || 0;

      if (p3 >= p2 && p3 >= p1) newResult.scoring.pleomorphismScore = 3;
      else if (p2 >= p1 && p2 >= p3) newResult.scoring.pleomorphismScore = 2;
      else newResult.scoring.pleomorphismScore = 1;

      let mCount = newResult.report.mitosis.count || 0;
      newResult.scoring.mitosisScore = mCount < 9 ? 1 : (mCount <= 17 ? 2 : 3);

      newResult.scoring.totalScore = newResult.scoring.tubulesScore + newResult.scoring.pleomorphismScore + newResult.scoring.mitosisScore;
      if (newResult.scoring.totalScore >= 8) newResult.scoring.grade = 'Grade 3';
      else if (newResult.scoring.totalScore >= 6) newResult.scoring.grade = 'Grade 2';
      else newResult.scoring.grade = 'Grade 1';

      return newResult;
    },

    getTermColorByName(name) {
      const term = this.allTerms.find(t => t.name === name);
      return term ? term.color : null;
    },
    async syncResult(jobId) {
      // 显示进度提示，告知用户这是一个可能耗时的操作
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });

      try {
        // 使用Cytomine.instance.api.get而不是this.$http.get，使用jobId作为路径参数
        const response = await Cytomine.instance.api.get(`job/${jobId}/result`);

        // 检查响应是否成功
        if (response) {
          this.$buefy.toast.open({
            message: this.$t('notif-success-results-synchronized'),
            type: 'is-success'
          });

          // 重新加载历史任务以更新结果
          await this.loadHistoricalJobs();
        } else {
          // 如果响应为空，显示错误信息
          console.error('Failed to synchronize results: Empty response');
          this.$buefy.toast.open({
            message: this.$t('notif-error-results-sync-empty-response'),
            type: 'is-danger'
          });
        }
      } catch (error) {
        console.error('Failed to synchronize results:', error);

        // 根据错误类型显示不同信息
        if (error.response) {
          // 服务器返回了错误状态码
          if (error.response.status === 404) {
            this.$buefy.toast.open({
              message: this.$t('notif-error-job-not-found'),
              type: 'is-danger'
            });
          } else if (error.response.status >= 500) {
            this.$buefy.toast.open({
              message: this.$t('notif-error-results-sync-server'),
              type: 'is-danger'
            });
          } else {
            this.$buefy.toast.open({
              message: this.$t('notif-error-results-sync-status', { status: error.response.status }),
              type: 'is-danger'
            });
          }
        } else if (error.request) {
          // 请求已发出但没有收到响应，可能是网络问题或服务器宕机
          this.$buefy.toast.open({
            message: this.$t('notif-error-network-unreachable'),
            type: 'is-danger'
          });
        } else {
          // 其他错误
          this.$buefy.toast.open({
            message: this.$t('notif-error-unexpected'),
            type: 'is-danger'
          });
        }
      } finally {
        // 确保无论成功还是失败都要关闭加载指示器
        loadingComponent.close();
      }
    },
    async loadHistoricalJobs() {
      const requestId = ++this.jobLoadRequestId;
      const imageId = this.imageId;
      const imageModule = this.imageModule;

      if (!imageId) {
        this.resetImageScopedState();
        return;
      }

      try {
        if (this.useMockAIJobResults) {
          const mockJobs = [
            {
              id: 11465,
              sessionId: 'a2849a35-eea0-464e-ab62-a0d846e6d624',
              airunnerId: 65,
              runnerName: 'Prostate Biopsy Gleason Grading — WSI Inference Pipeline',
              projectId: 3139,
              imageId: this.imageId,
              parameters: 'null',
              source: 'mock/prostate-biopsy/sample.svs',
              imageServer: 'mock-server',
              statusCode: 200,
              status: 'Completed',
              message: this.$t('processing-completed-with-annotations'),
              data: JSON.stringify({
                benign: {
                  area: 11.284,
                  percentage: 40.7
                },
                stroma: {
                  area: 2.146,
                  percentage: 7.7
                },
                gleason3: {
                  area: 5.321,
                  percentage: 37.3
                },
                gleason4: {
                  area: 4.118,
                  percentage: 28.8
                },
                gleason5: {
                  area: 4.843,
                  percentage: 33.9
                }
              }),
              created: 1776908133140,
              updated: 1776908543715
            },
            {
              id: 11466,
              sessionId: 'dbeef5a1-1f1f-4cc2-8446-cf7e9d04318b',
              airunnerId: 66,
              runnerName: 'Prostate Biopsy Gleason Grading — WSI Inference Pipeline',
              projectId: 3139,
              imageId: this.imageId,
              parameters: 'null',
              source: 'mock/prostate-biopsy/count-based-warning.svs',
              imageServer: 'mock-server',
              statusCode: 200,
              status: 'Completed',
              message: this.$t('processing-completed-with-raw-count-payload'),
              data: JSON.stringify({
                benign: {
                  count: 472,
                  percentage: 40.8
                },
                gleason3: {
                  count: 223,
                  percentage: 19.3
                },
                gleason4: {
                  count: 173,
                  percentage: 15.0
                },
                gleason5: {
                  count: 289,
                  percentage: 25.0
                }
              }),
              created: 1776908188140,
              updated: 1776908593715
            },
            {
              id: 11467,
              sessionId: 'dbeef5a1-1f1f-4cc2-8446-cf7e9d04318b',
              airunnerId: 66,
              runnerName: 'Breast Cancer Nottingham Grading — WSI Inference Pipeline',
              projectId: 3139,
              imageId: this.imageId,
              parameters: 'null',
              source: 'mock/nottingham/sample.svs',
              imageServer: 'mock-server',
              statusCode: 200,
              status: 'Completed',
              message: this.$t('processing-completed-with-annotations'),
              data: JSON.stringify({
                tubules: {
                  count: 1824,
                  percentage: 68.4
                },
                mitosis: {
                  count: 14,
                  percentage: 5.2
                },
                invasive: {
                  count: 641,
                  percentage: 24.1
                },
                'Pleomorphism Score 1 Nuclei': {
                  count: 248,
                  percentage: 18.7
                },
                'Pleomorphism Score 2 Nuclei': {
                  count: 731,
                  percentage: 55.1
                },
                'Pleomorphism Score 3 Nuclei': {
                  count: 348,
                  percentage: 26.2
                }
              }),
              created: 1776908233140,
              updated: 1776908643715
            }
          ];

          if (this.useMockMalariaJobResult) {
            mockJobs.push(this.createMockMalariaJob());
          }
          if (this.shouldUseMockLeprosyJobResult()) {
            mockJobs.push(this.createMockLeprosyJob());
          }

          this.activeJobs = [];
          this.failedJobs = [];
          this.aiResults = [];

          for (const job of mockJobs) {
            if (requestId !== this.jobLoadRequestId || imageId !== this.imageId) {
              return;
            }
            const parsedData = JSON.parse(job.data);
            await this.integrateJobResult(job.id, job.runnerName, parsedData, job.needsResultSync);
          }
          this.persistImageScopedCache();
          return;
        }

        let response = await Cytomine.instance.api.get(`ai-algorithm-jobs?imageId=${imageId}`);
        if (requestId !== this.jobLoadRequestId || imageId !== this.imageId) {
          return;
        }

        let jobs = response.data;
        if (response.data.collection) jobs = response.data.collection;
        else if (!Array.isArray(jobs)) jobs = [jobs];

        if (this.useMockMalariaJobResult) {
          jobs = [...jobs, this.createMockMalariaJob()];
        }
        if (this.shouldUseMockLeprosyJobResult()) {
          jobs = [...jobs, this.createMockLeprosyJob()];
        }

        const hasCompletedJobs = jobs.some(job => this.isCompletedJobStatus(job.status));
        if (hasCompletedJobs) {
          try {
            await this.$store.dispatch(imageModule + 'refreshData');
          } catch (error) {
            console.error('Error refreshing image data after completed AI jobs:', error);
          }
        }

        // Clear tracking arrays fully before re-evaluating
        this.activeJobs = [];
        this.failedJobs = [];
        this.aiResults = [];

        for (const job of jobs) {
          let runnerName = job.runnerName || `AI Runner ${job.airunnerId}`;
          if (this.aiRunners && this.aiRunners.length > 0) {
            const runner = this.aiRunners.find(r => r.id === job.airunnerId);
            if (runner) runnerName = runner.name;
          }

          if (this.isActiveJobStatus(job.status)) {
            this.activeJobs.push({
              sessionId: job.sessionId || job.id,
              runnerName: runnerName
            });
          } else if (this.isCompletedJobStatus(job.status)) {
            let parsedData = null;
            if (job.data) {
              try {
                parsedData = JSON.parse(job.data);
              } catch (e) {
                console.error("Error parsing job data:", e);
              }
            }
            await this.integrateJobResult(job.id, runnerName, parsedData, job.needsResultSync);
          } else if (this.isFailedJob(job)) {
            // Only Error or Failed jobs go here with timestamp and message
            let jobTime = new Date(job.updated || job.created || Date.now()).toLocaleString();
            this.failedJobs.push({
              sessionId: job.sessionId || job.id,
              runnerName: runnerName,
              time: jobTime,
              errorMessage: job.message || this.$t('verification-failed')
            });
          }
        }
        this.persistImageScopedCache();
      } catch (error) {
        console.error('Error fetching historical jobs:', error);
        if (this.useMockMalariaJobResult || this.shouldUseMockLeprosyJobResult()) {
          this.activeJobs = [];
          this.failedJobs = [];
          this.aiResults = [];
          const mockJobs = [];
          if (this.useMockMalariaJobResult) {
            mockJobs.push(this.createMockMalariaJob());
          }
          if (this.shouldUseMockLeprosyJobResult()) {
            mockJobs.push(this.createMockLeprosyJob());
          }
          for (const job of mockJobs) {
            await this.integrateJobResult(job.id, job.runnerName, JSON.parse(job.data), false);
          }
          this.persistImageScopedCache();
        }
      }
    },
    formatAiWarning(warning) {
      if (!warning) return '';
      if (typeof warning === 'string') return warning;
      if (warning.messageKey) {
        return this.$t(warning.messageKey, warning.messageParams || {});
      }
      return warning.message || '';
    },

    normalizedJobStatus(status) {
      return String(status || '').trim().toLowerCase();
    },

    isActiveJobStatus(status) {
      return ['processing', 'running', 'starting'].includes(this.normalizedJobStatus(status));
    },

    isCompletedJobStatus(status) {
      return this.normalizedJobStatus(status) === 'completed';
    },

    isFailedJob(job) {
      const status = this.normalizedJobStatus(job.status);
      return ['error', 'failed', 'failure'].includes(status) || Number(job.statusCode) >= 400;
    }
  },

  beforeDestroy() {
    this.clearFakeLeprosyJobTimers();
  },

  async mounted() {
    const restored = this.restoreImageScopedCache();
    if (restored) {
      this.refreshMalariaResultsFromStore();
    }
    if (!restored || !this.aiRunners.length) {
      await this.fetchAIRunners();
    } else {
      this.fetchAIRunners();
    }
    if (!restored || !this.aiResults.length) {
      await this.loadHistoricalJobs();
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';
@import '../../assets/styles/colors.scss';

.panel {
  max-width: 100%;
  border: 1px solid rgba(190, 214, 238, 0.13);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(222, 238, 255, 0.04), rgba(222, 238, 255, 0.018));
  color: $dark-text-primary;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.panel:not(:last-child) {
  margin-bottom: 5px;
}

@media (max-width: 768px) {
  .panel {
    width: 100%;
    border-radius: 0;
    border: none;
  }
}

.panel-title {
  padding: 0.76rem 0.85rem;
  font-weight: 700;
  border-bottom: 1px solid rgba(190, 214, 238, 0.11);
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  background: rgba(222, 238, 255, 0.035);
  color: $dark-text-primary;
  align-items: center;
  transition: background-color 0.16s ease;
}

.panel-title:hover {
  background-color: rgba($primary, 0.08);
}

.panel-title-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.panel-name {
  font-weight: 700;
  line-height: 1.25;
}

.panel+.panel {
  margin-top: 2px;
}

.arrow {
  color: $dark-text-disabled;
  transition: transform 0.18s ease, color 0.18s ease;
}

.arrow.open {
  transform: rotate(90deg);
  color: $primary;
}

.panel-body {
  padding: 0.78rem 0.85rem;
  background: rgba(8, 17, 29, 0.18);
}

.ai-runner-selection {
  display: flex;
  gap: 0.65rem;
  align-items: center;
  margin-bottom: 0.85rem;
  padding: 0.75rem;
  border: 1px solid rgba(190, 214, 238, 0.12);
  border-radius: 8px;
  background: rgba(222, 238, 255, 0.028);
}

.ai-runner-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;
}

.failed-jobs-button {
  position: relative;
}

.active-jobs {
  display: grid;
  gap: 0.45rem;
  margin-bottom: 0.85rem;
}

.active-job {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0;
  padding: 0.5rem 0.65rem;
  border: 1px solid rgba($info, 0.3);
  border-radius: 7px;
  background: rgba($info, 0.085);
  font-size: 0.78rem;
  color: #b4d6ff;
}

.switch {
  position: relative;
  width: 42px;
  height: 22px;
  display: inline-block;
}

.switch input {
  display: none;
}

.switch-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(132, 156, 184, 0.16);
  border: 1px solid rgba(190, 214, 238, 0.15);
  border-radius: 20px;
  transition: 0.2s;
}

.switch-slider:before {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 1px;
  background: $dark-text-secondary;
  border-radius: 50%;
  transition: 0.2s;
}

input:checked+.switch-slider {
  background: $primary;
  border-color: $primary;
}

input:checked+.switch-slider:before {
  transform: translateX(19px);
}

.ai-result-warning {
  margin-bottom: 0.75rem;
}

.ai-result-warning,
.ai-result-warning .message-body {
  background: rgba($danger, 0.12);
  color: #ffb5c5;
}

.ai-result-warning .message-body {
  padding: 0.75rem 0.9rem;
  font-size: 0.85rem;
  border-color: rgba($danger, 0.42);
  border-radius: 7px;
}
</style>
